const isArray = (a: unknown): a is unknown[] => {
  return Array.isArray(a);
};

export const isObject = (o: unknown): o is Record<string, unknown> => {
  return o === Object(o) && !isArray(o) && typeof o !== "function";
};

// Only walk plain data. Class instances (Signals, Dates, DOM nodes) can be
// cyclic and must pass through untouched.
const isPlainObject = (o: unknown): o is Record<string, unknown> => {
  if (!isObject(o)) return false;
  const proto = Object.getPrototypeOf(o);
  return proto === Object.prototype || proto === null;
};

export const toCamel = (s: string): string => {
  return s.replace(/([-_][a-z])/gi, ($1) => {
    return $1.toUpperCase().replace("-", "").replace("_", "");
  });
};

export const toSnake = (s: string): string => {
  return s.replace(/([A-Z])/g, "_$1").toLowerCase();
};

export const toKebab = (s: string): string => {
  return s.replace(/([A-Z])/g, "-$1").toLowerCase();
};

// Generic identity signature: these recursively re-key an arbitrary
// object/array (deep camelCase or snake_case conversion), so the output is
// structurally the same shape as the input as far as every caller is
// concerned (they immediately treat the result as the same type they passed
// in, or cast it themselves). `T` lets each call site keep the type it
// already relied on instead of collapsing to `unknown`.
export const keysToCamel = <T = unknown>(o: T): T => {
  if (isPlainObject(o)) {
    const n: Record<string, unknown> = {};
    Object.keys(o).forEach((k: string) => {
      n[toCamel(k)] = keysToCamel(o[k]);
    });
    return n as T;
  } else if (isArray(o)) {
    return o.map((i: unknown) => {
      return keysToCamel(i);
    }) as T;
  }
  return o;
};

export const keysToSnake = <T = unknown>(o: T): T => {
  if (isPlainObject(o)) {
    const n: Record<string, unknown> = {};
    Object.keys(o).forEach((k: string) => {
      n[toSnake(k)] = keysToSnake(o[k]);
    });
    return n as T;
  } else if (isArray(o)) {
    return o.map((i: unknown) => {
      return keysToSnake(i);
    }) as T;
  }
  return o;
};
